import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Row, Col, InputGroup, Pagination } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaTrash, FaEdit, FaPlus, FaSearch, FaTimes, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';
import * as api from '~/Services/Service';

const ITENS_POR_PAGINA = 20;

const VariantesAtivo = () => {
  const temPermissao = AuthStorage.temCaracteristicas([caracteristicas.AtualizaAtivo]);
  const [variantes, setVariantes] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingAcao, setLoadingAcao] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [varianteEditando, setVarianteEditando] = useState(null);
  const [nomeVariante, setNomeVariante] = useState('');
  const [obraVariante, setObraVariante] = useState('N');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [varianteParaExcluir, setVarianteParaExcluir] = useState(null);

  const [ordenacao, setOrdenacao] = useState({ campo: 'nome', direcao: 'asc' });
  const [pagina, setPagina] = useState(1);

  const thSortStyle = { cursor: 'pointer', userSelect: 'none' };

  const handleOrdenacao = (campo) => {
    setOrdenacao(prev => {
      if (prev.campo === campo) {
        return { campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo, direcao: 'asc' };
    });
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  useEffect(() => { carregarVariantes(); }, []);

  const carregarVariantes = async () => {
    setLoading(true);
    try {
      const res = await api.listarVariantesAtivo();
      setVariantes(res || []);
    } catch (e) {
      toast.error('Erro ao carregar variantes de ativos');
    } finally {
      setLoading(false);
    }
  };

  const handleNovaVariante = () => {
    setVarianteEditando(null);
    setNomeVariante('');
    setObraVariante('N');
    setShowModal(true);
  };

  const handleEditarVariante = (variante) => {
    setVarianteEditando(variante);
    setNomeVariante(variante.nome);
    setObraVariante(variante.obra);
    setShowModal(true);
  };

  const handleSalvarVariante = async () => {
    if (!nomeVariante.trim()) { toast.warn('Descrição é obrigatória'); return; }
    if (nomeVariante.trim().length > 30) { toast.warn('Máximo 30 caracteres'); return; }
    setLoadingAcao(true);
    try {
      if (varianteEditando) {
        await api.atualizarVarianteAtivo(varianteEditando.codigo, {
          nome: nomeVariante.trim(),
          obra: obraVariante,
        });
        toast.success('Variante atualizada com sucesso!');
      } else {
        await api.incluirVarianteAtivo({ nome: nomeVariante.trim(), obra: obraVariante });
        toast.success('Variante criada com sucesso!');
      }
      setShowModal(false);
      const res = await api.listarVariantesAtivo();
      const lista = res || [];
      setVariantes(lista);
      if (!varianteEditando) {
        const ultimaPagina = Math.ceil(lista.length / ITENS_POR_PAGINA) || 1;
        setPagina(ultimaPagina);
      }
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao salvar variante.');
    } finally {
      setLoadingAcao(false);
    }
  };

  const handleConfirmarExclusao = (variante) => {
    setVarianteParaExcluir(variante);
    setShowDeleteModal(true);
  };

  const handleExcluirVariante = async () => {
    setLoadingAcao(true);
    try {
      await api.excluirVarianteAtivo(varianteParaExcluir.codigo);
      toast.success('Variante excluída com sucesso!');
      setShowDeleteModal(false);
      setFiltro('');
      setPagina(1);
      await carregarVariantes();
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao excluir variante.');
    } finally {
      setLoadingAcao(false);
    }
  };

  const variantesFiltradas = variantes
    .filter(v => {
      if (!filtro) return true;
      const termo = filtro.toLowerCase();
      return String(v.codigo).indexOf(termo) >= 0 || (v.nome && v.nome.toLowerCase().indexOf(termo) >= 0);
    })
    .slice()
    .sort((a, b) => {
      const dir = ordenacao.direcao === 'asc' ? 1 : -1;
      if (ordenacao.campo === 'nome') {
        return dir * String(a.nome || '').toLowerCase().localeCompare(String(b.nome || '').toLowerCase());
      }
      return dir * (a.codigo - b.codigo);
    });

  const totalPaginas = Math.ceil(variantesFiltradas.length / ITENS_POR_PAGINA) || 1;
  const dadosPaginados = variantesFiltradas.slice((pagina - 1) * ITENS_POR_PAGINA, pagina * ITENS_POR_PAGINA);

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;

    const itens = [];
    const maxBotoes = 5;

    itens.push(
      <Pagination.First key="first" onClick={() => setPagina(1)} disabled={pagina === 1} />
    );
    itens.push(
      <Pagination.Prev key="prev" onClick={() => setPagina(pagina - 1)} disabled={pagina === 1} />
    );

    let inicioPaginas = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    let fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = inicioPaginas; i <= fimPaginas; i++) {
      const num = i;
      itens.push(
        <Pagination.Item key={num} active={num === pagina} onClick={() => setPagina(num)}>
          {num}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next key="next" onClick={() => setPagina(pagina + 1)} disabled={pagina === totalPaginas} />
    );
    itens.push(
      <Pagination.Last key="last" onClick={() => setPagina(totalPaginas)} disabled={pagina === totalPaginas} />
    );

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>
        <small className="text-muted">
          Mostrando {dadosPaginados.length > 0 ? (pagina - 1) * ITENS_POR_PAGINA + 1 : 0}
          {' até '}{Math.min(pagina * ITENS_POR_PAGINA, variantesFiltradas.length)}{' de '}{variantesFiltradas.length}{' registros'}
        </small>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  if (loading) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <style>{`
        .va-desktop { display: block; }
        .va-mobile  { display: none; }
        @media (max-width: 768px) {
          .va-desktop { display: none !important; }
          .va-mobile  { display: block; }
          .va-filtro-row { flex-direction: column; }
          .va-filtro-row .text-right { text-align: right !important; margin-top: 8px; }
        }
      `}</style>

      <Header title="Variantes de Ativos" />

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row className="align-items-end">
            <Col xs={12} md={5}>
              <Form.Group controlId="filtro.busca" className="mb-md-0 mb-2">
                <Form.Label>Busca</Form.Label>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text><FaSearch /></InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="text"
                    placeholder="Código ou descrição..."
                    value={filtro}
                    onChange={e => { setFiltro(e.target.value); setPagina(1); }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs={12} md={7}>
              <Form.Group className="d-flex justify-content-end flex-wrap mb-0" style={{ gap: '8px' }}>
                {filtro && (
                  <Button variant="outline-secondary" onClick={() => { setFiltro(''); setPagina(1); }}>
                    <FaTimes className="mr-1" /> Limpar
                  </Button>
                )}
                {temPermissao && (
                  <Button variant="success" onClick={handleNovaVariante}>
                    <FaPlus className="mr-1" /> Nova Variante
                  </Button>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Variantes de Ativos
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({variantesFiltradas.length} {variantesFiltradas.length === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
        </Card.Header>
        <Card.Body className="p-0">
          {variantesFiltradas.length === 0 ? (
            <p className="text-muted text-center my-4">
              {filtro ? 'Nenhuma variante encontrada para o filtro informado.' : 'Nenhuma variante cadastrada.'}
            </p>
          ) : (
            <>
              <div className="va-desktop">
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ ...thSortStyle, width: '100px', whiteSpace: 'nowrap' }} onClick={() => handleOrdenacao('codigo')}>
                        Código{iconeOrdenacao('codigo')}
                      </th>
                      <th style={{ ...thSortStyle, whiteSpace: 'nowrap' }} onClick={() => handleOrdenacao('nome')}>
                        Descrição{iconeOrdenacao('nome')}
                      </th>
                      <th style={{ width: '140px', whiteSpace: 'nowrap' }}>
                        Diário de Obras
                      </th>
                      {temPermissao && (
                        <th style={{ width: '100px', textAlign: 'center' }}>Ações</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {dadosPaginados.map(variante => (
                      <tr key={variante.codigo}>
                        <td>{variante.codigo}</td>
                        <td>{variante.nome}</td>
                        <td>{variante.obra === 'S' ? 'Sim' : 'Não'}</td>
                        {temPermissao && (
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <Button size="sm" variant="outline-primary" title="Editar" className="mr-1" onClick={() => handleEditarVariante(variante)}>
                              <FaEdit />
                            </Button>
                            <Button size="sm" variant="outline-danger" title="Excluir" onClick={() => handleConfirmarExclusao(variante)}>
                              <FaTrash />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="va-mobile p-2">
                {dadosPaginados.map(variante => (
                  <Card key={variante.codigo} className="mb-2 shadow-sm">
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong style={{ fontSize: '0.95rem' }}>{variante.nome}</strong>
                          <br />
                          <small className="text-muted">Código: {variante.codigo}</small>
                          <br />
                          <small className="text-muted">Diário de Obras: {variante.obra === 'S' ? 'Sim' : 'Não'}</small>
                        </div>
                      </div>
                      {temPermissao && (
                        <div className="d-flex justify-content-between">
                          <Button size="sm" variant="outline-primary" onClick={() => handleEditarVariante(variante)}>
                            <FaEdit className="mr-1" /> Editar
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => handleConfirmarExclusao(variante)}>
                            <FaTrash className="mr-1" /> Excluir
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
              {renderizarPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }} className="modal-header-blue">
          <Modal.Title style={{ color: '#fff' }}>{varianteEditando ? 'Editar Variante de Ativo' : 'Nova Variante de Ativo'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {varianteEditando && (
            <Form.Group>
              <Form.Label>Código</Form.Label>
              <p className="form-control-static mb-0" style={{ paddingTop: '7px', paddingBottom: '7px' }}>
                {varianteEditando.codigo}
              </p>
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              type="text"
              value={nomeVariante}
              maxLength={30}
              placeholder="Descrição da variante (máx. 30 caracteres)"
              onChange={e => setNomeVariante(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSalvarVariante(); }}
              autoFocus
            />
            <Form.Text className="text-muted">{nomeVariante.length}/30</Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>Aparece no diário de obras</Form.Label>
            <Form.Control
              as="select"
              value={obraVariante}
              onChange={e => setObraVariante(e.target.value)}
            >
              <option value="N">Não</option>
              <option value="S">Sim</option>
            </Form.Control>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSalvarVariante} disabled={loadingAcao}>
            {loadingAcao ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Excluir Variante de Ativo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Deseja realmente excluir a variante{' '}
          <strong>
            {varianteParaExcluir ? `${varianteParaExcluir.codigo} - ${varianteParaExcluir.nome}` : ''}
          </strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleExcluirVariante} disabled={loadingAcao}>
            {loadingAcao ? 'Excluindo...' : 'Excluir'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default VariantesAtivo;
